import { useState } from 'react';
import { useDelete, useOne } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router';
import { Button, Space } from 'antd';
import { DetailField, DetailPage, useConfirmAction } from '@viq-admin/ui';
import type { PromptTemplate } from '@viq-admin/types';
import { PIPELINE_LABELS } from '../../api/promptStore';
import { Can } from '../../ability/AbilityContext';
import { PromptTemplatesEdit } from './edit';
import { AppliedCompaniesModal } from './AppliedCompaniesModal';

export interface PromptTemplateDetailProps {
  id?: string;
}

/**
 * Prompt template detail — read-only by default, Edit flips the form in
 * place (via the shared DetailPage). One route, /prompt-templates/:id.
 * Mirrors `ToolkitDetail`'s structure; see that file's header comment for
 * the 404-derivation rationale (a missing record surfaces through useOne as
 * `query.error` with `statusCode === 404`, never an empty success).
 *
 * Prompt templates are global reusable append snippets — there is no archive
 * lifecycle here (same as toolkits).
 *
 * "View companies" opens `AppliedCompaniesModal` (view/apply/detach the
 * companies this template's append is applied to) and is NOT
 * capability-gated — it's a read action, and the modal's own mutations
 * (apply / detach / reapply) are gated internally on `manage PromptTemplate`.
 * Delete is gated the same way as the toolkits precedent's relocated header
 * action.
 *
 * `id` is accepted as a prop, falling back to the route param (like
 * `PromptTemplatesEdit`), so a future test can render
 * `<PromptTemplateDetail id="…" />` directly without a router context.
 */
export function PromptTemplateDetail({ id: idProp }: PromptTemplateDetailProps = {}) {
  const params = useParams<{ id: string }>();
  const id = idProp ?? params.id;
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [companiesOpen, setCompaniesOpen] = useState(false);
  const confirm = useConfirmAction();

  const { result: record, query } = useOne<PromptTemplate>({
    resource: 'prompt-templates',
    id,
    queryOptions: { enabled: !!id },
  });
  const { mutateAsync: deleteTemplate } = useDelete();

  const error = query?.error as { statusCode?: number } | null | undefined;
  const notFound = error?.statusCode === 404;

  function handleDelete() {
    confirm({
      title: 'Delete this prompt template?',
      content:
        'This only removes the reusable template. It is refused while any company still has it applied — detach it from them first (see "Applied companies").',
      okText: 'Delete',
      danger: true,
      successMessage: 'Prompt template deleted.',
      onConfirm: async () => {
        await deleteTemplate({
          resource: 'prompt-templates',
          id: id!,
          // The confirm dialog above owns the success/error toast — suppress
          // Refine's own notification provider so the operator does not see
          // it twice (matches ToolkitDetail's delete action / CompanyDetail).
          successNotification: false,
          errorNotification: false,
        });
        navigate('/prompt-templates');
      },
    });
  }

  const headerActions = (
    <Space>
      <Button onClick={() => setCompaniesOpen(true)}>View companies</Button>
      <Can I="manage" a="PromptTemplate" passThrough>
        {(allowed: boolean) =>
          allowed ? (
            <Button danger onClick={handleDelete}>
              Delete
            </Button>
          ) : null
        }
      </Can>
    </Space>
  );

  const readView = record ? (
    <Space
      direction="vertical"
      size="large"
      style={{ width: '100%' }}
      data-testid="prompt-template-read-view"
    >
      <DetailField label="Name" value={record.name} />
      <DetailField label="Pipeline" value={PIPELINE_LABELS[record.pipeline]} />
      <DetailField label="Applied to" value={`${record.appliedCompanyCount} company(ies)`} />
      <DetailField
        label="Append body"
        value={
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
            {record.body}
          </pre>
        }
      />
      <DetailField label="Created" value={new Date(record.createdAt).toLocaleDateString()} />
      <DetailField label="Updated" value={new Date(record.updatedAt).toLocaleDateString()} />
    </Space>
  ) : null;

  return (
    <>
      <DetailPage
        title={record?.name ?? 'Prompt template'}
        breadcrumbs={[
          { label: 'AI Prompt Templates', href: '/prompt-templates' },
          { label: record?.name ?? '' },
        ]}
        onNavigate={({ href }) => navigate(href)}
        onBack={() => navigate('/prompt-templates')}
        loading={query?.isLoading}
        error={notFound ? undefined : query?.error}
        notFound={notFound}
        notFoundHref="/prompt-templates"
        editing={editing}
        onEditingChange={(next) => {
          setEditing(next);
          // Leaving edit by ANY path (Cancel, back, a breadcrumb) must clear
          // `dirty` — not just the `onSaved` success path below. Otherwise a
          // dirty-then-discarded session leaves `dirty` stuck true, and the
          // NEXT edit session (freshly remounted, genuinely pristine) still
          // trips the discard guard on its first Cancel.
          if (!next) setDirty(false);
        }}
        editCap="manage:PromptTemplate"
        dirty={dirty}
        actions={headerActions}
        readView={readView}
        editView={
          <PromptTemplatesEdit
            id={id}
            onDirtyChange={setDirty}
            onSaved={() => {
              setDirty(false);
              setEditing(false);
            }}
          />
        }
      />
      {companiesOpen && (
        <AppliedCompaniesModal
          templateId={id!}
          open
          onClose={() => setCompaniesOpen(false)}
        />
      )}
    </>
  );
}
